import { useRoomContext, useTracks } from "@livekit/components-react"
import * as Sentry from "@sentry/react"
import { RemoteVideoTrack, Track } from "livekit-client"
import { RequestOptions } from "mixpanel-browser"
import { useEffect } from "react"
import { AnalyticsEvents } from "../../types/Analytics"
import { useMixpanel } from "./useMixpanel"

const POLL_INTERVAL_MS = 1000
// Long enough that the viewer sees the avatar stop, not a dropped frame or two
const FREEZE_THRESHOLD_MS = 2000

/**
 * Watch the avatar's video for stalls in decoded frames while the tab is
 * visible. The first freeze of a track is reported to Sentry, and a summary
 * of all of them is sent to Mixpanel when the track or the page goes away.
 */
export const useReportAvatarFreezes = () => {
  const room = useRoomContext()
  const { logAnalyticsEvent } = useMixpanel()

  const avatarTrack = useTracks([Track.Source.Camera], {
    onlySubscribed: true,
  }).find((track) => track.participant.isAgent)

  const videoTrack = avatarTrack?.publication.videoTrack
  const avatarIdentity = avatarTrack?.participant.identity

  useEffect(() => {
    if (!(videoTrack instanceof RemoteVideoTrack)) return

    let isStopped = false
    let timeout: number | undefined
    // Null while the baseline is unknown: before the first poll, and after the
    // tab was hidden or the track muted, when frames stop for reasons of their own
    let statsAtLastAdvance: RTCInboundRtpStreamStats | null = null
    let lastAdvancedAt = 0
    let lastPolledAt = 0
    let isFrozen = false
    let freezeCount = 0
    let frozenMs = 0
    let longestFreezeMs = 0
    let watchedMs = 0

    const finishFreeze = (now: number) => {
      if (!isFrozen) return
      isFrozen = false
      const freezeMs = now - lastAdvancedAt
      frozenMs += freezeMs
      longestFreezeMs = Math.max(longestFreezeMs, freezeMs)
    }

    // Read the browser's report rather than livekit-client's getReceiverStats,
    // which leaves out the pause and keyframe counters that tell the causes apart
    const readStats = async () => {
      let report: RTCStatsReport | undefined
      try {
        report = await videoTrack.receiver?.getStats()
      } catch (error) {
        // Stats can fail while the connection reconnects or closes; skip this poll
        console.warn("Failed to read avatar video stats", error)
        return undefined
      }
      return [...(report?.values() ?? [])].find(
        (stats): stats is RTCInboundRtpStreamStats =>
          stats.type === "inbound-rtp",
      )
    }

    const poll = async () => {
      if (document.visibilityState !== "visible" || videoTrack.isMuted) {
        finishFreeze(performance.now())
        statsAtLastAdvance = null
        return
      }

      const stats = await readStats()
      // Cleanup may have run, and sent the summary, while the stats were pending
      if (isStopped || stats === undefined) return

      const now = performance.now()
      if (statsAtLastAdvance === null) {
        statsAtLastAdvance = stats
        lastAdvancedAt = now
        lastPolledAt = now
        return
      }

      watchedMs += now - lastPolledAt
      lastPolledAt = now

      if (stats.framesDecoded !== statsAtLastAdvance.framesDecoded) {
        finishFreeze(now)
        statsAtLastAdvance = stats
        lastAdvancedAt = now
        return
      }

      if (isFrozen || now - lastAdvancedAt < FREEZE_THRESHOLD_MS) return
      isFrozen = true
      freezeCount += 1
      if (freezeCount > 1) return

      const before = statsAtLastAdvance
      const getIncrease = (
        after: number | undefined,
        previous: number | undefined,
      ) =>
        after === undefined || previous === undefined
          ? undefined
          : after - previous

      Sentry.captureMessage("Avatar video freeze", {
        level: "warning",
        fingerprint: ["avatar-video-freeze"],
        tags: {
          call_id: room.name,
          avatar_identity: avatarIdentity,
          livekit_region: room.serverInfo?.region,
        },
        extra: {
          livekitNodeId: room.serverInfo?.nodeId,
          frozenMs: now - lastAdvancedAt,
          framesDecoded: stats.framesDecoded,
          framesDropped: stats.framesDropped,
          packetsReceived: stats.packetsReceived,
          packetsLost: stats.packetsLost,
          jitterSeconds: stats.jitter,
          frameWidth: stats.frameWidth,
          frameHeight: stats.frameHeight,
          decoderImplementation: stats.decoderImplementation,
          keyFramesDecoded: stats.keyFramesDecoded,
          nackCount: stats.nackCount,
          pauseCount: stats.pauseCount,
          totalPausesDurationSeconds: stats.totalPausesDuration,
          browserFreezeCount: stats.freezeCount,
          browserTotalFreezesDurationSeconds: stats.totalFreezesDuration,
          // Packets alone do not show media arriving: the SFU sends padding-only
          // packets to probe bandwidth (WritePaddingRTP in livekit's
          // pkg/sfu/downtrack.go), and only bytesReceived leaves the padding out
          packetsReceivedWhileFrozen: getIncrease(
            stats.packetsReceived,
            before.packetsReceived,
          ),
          bytesReceivedWhileFrozen: getIncrease(
            stats.bytesReceived,
            before.bytesReceived,
          ),
          packetsLostWhileFrozen: getIncrease(
            stats.packetsLost,
            before.packetsLost,
          ),
          framesReceivedWhileFrozen: getIncrease(
            stats.framesReceived,
            before.framesReceived,
          ),
          pliCountWhileFrozen: getIncrease(stats.pliCount, before.pliCount),
          pauseCountWhileFrozen: getIncrease(
            stats.pauseCount,
            before.pauseCount,
          ),
        },
      })
    }

    const schedule = () => {
      timeout = window.setTimeout(async () => {
        try {
          await poll()
        } finally {
          if (!isStopped) schedule()
        }
      }, POLL_INTERVAL_MS)
    }

    const stop = (requestOptions?: RequestOptions) => {
      if (isStopped) return
      isStopped = true
      window.clearTimeout(timeout)
      finishFreeze(performance.now())
      if (watchedMs === 0) return
      logAnalyticsEvent(
        AnalyticsEvents.AvatarVideoSummary,
        {
          call_id: room.name,
          livekit_region: room.serverInfo?.region,
          freeze_count: freezeCount,
          frozen_seconds: frozenMs / 1000,
          longest_freeze_seconds: longestFreezeMs / 1000,
          watched_seconds: watchedMs / 1000,
        },
        requestOptions,
      )
    }

    // React cleanup never runs when the tab closes or the embed is removed, so
    // send the summary as a beacon that survives the page going away
    const handlePageHide = () =>
      stop({ transport: "sendBeacon", send_immediately: true })

    window.addEventListener("pagehide", handlePageHide)
    schedule()

    return () => {
      window.removeEventListener("pagehide", handlePageHide)
      stop()
    }
  }, [videoTrack])
}
